import { cn } from '@/lib/utils';
import React from 'react';
import { AppLogo } from '@/components/shared/display/Logo';

interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  text?: string;
}

// Razor Labs 3D Cube Logo Spinner
export const Spinner = React.memo(({ size = 'md', className, text, ...props }: SpinnerProps) => {
  const sizes = {
    sm: 50,
    md: 80,
    lg: 100,
    xl: 140,
  };

  const s = sizes[size];
  const cubeSize = s * 0.65; // Bigger cube relative to container

  return (
    <div className={cn('flex flex-col items-center justify-center gap-4', className)} {...props}>
      <style>{`
        @keyframes spin3d-cube {
          0% {
            transform: rotateX(54.7deg) rotateZ(45deg) rotateY(0deg);
          }
          100% {
            transform: rotateX(54.7deg) rotateZ(45deg) rotateY(360deg);
          }
        }
        
        .cube-face {
          position: absolute;
          width: ${cubeSize}px;
          height: ${cubeSize}px;
          border: 2px solid;
          background: transparent;
        }
        
        .cube-front {
          transform: rotateY(0deg) translateZ(${cubeSize / 2}px);
          border: 1px solid rgba(112, 113, 243, 0.2);
          background: linear-gradient(135deg, rgba(112, 113, 243, 0.6), rgba(141, 141, 245, 0.6));
        }
        
        .cube-back {
          transform: rotateY(180deg) translateZ(${cubeSize / 2}px);
          border: 1px solid rgba(102, 103, 221, 0.2);
          background: linear-gradient(135deg, rgba(102, 103, 221, 0.6), rgba(80, 80, 173, 0.6));
        }
        
        .cube-right {
          transform: rotateY(90deg) translateZ(${cubeSize / 2}px);
          border: 1px solid rgba(18, 255, 226, 0.2);
          background: linear-gradient(135deg, rgba(18, 255, 226, 0.6), rgba(65, 255, 232, 0.6));
        }
        
        .cube-left {
          transform: rotateY(-90deg) translateZ(${cubeSize / 2}px);
          border: 1px solid rgba(16, 232, 206, 0.2);
          background: linear-gradient(135deg, rgba(16, 232, 206, 0.6), rgba(13, 181, 160, 0.6));
        }
        
        .cube-top {
          transform: rotateX(90deg) translateZ(${cubeSize / 2}px);
          border: 1px solid rgba(159, 160, 247, 0.2);
          background: linear-gradient(135deg, rgba(159, 160, 247, 0.6), rgba(189, 190, 249, 0.6));
        }
        
        .cube-bottom {
          transform: rotateX(-90deg) translateZ(${cubeSize / 2}px);
          border: 1px solid rgba(96, 255, 236, 0.2);
          background: linear-gradient(135deg, rgba(96, 255, 236, 0.6), rgba(146, 255, 242, 0.6));
        }
      `}</style>

      <div style={{ perspective: '200px', width: s, height: s }}>
        <div
          style={{
            width: cubeSize,
            height: cubeSize,
            margin: 'auto',
            position: 'relative',
            transformStyle: 'preserve-3d',
            animation: 'spin3d-cube 3.5s linear infinite',
            transform: 'rotateX(54.7deg) rotateZ(45deg) rotateY(0deg)',
          }}
        >
          <div className="cube-face cube-front"></div>
          <div className="cube-face cube-back"></div>
          <div className="cube-face cube-right"></div>
          <div className="cube-face cube-left"></div>
          <div className="cube-face cube-top"></div>
          <div className="cube-face cube-bottom"></div>
        </div>
      </div>

      {text ? (
        <div className="text-sm font-medium text-gray-600 dark:text-gray-400 animate-pulse">{text}</div>
      ) : (
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center">
            <AppLogo size="100" />
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 animate-pulse">Tapping into the data flows...</div>
        </div>
      )}
    </div>
  );
});
Spinner.displayName = 'Spinner';

export const ScreenSpinner = React.memo(() => {
  return (
    <div className="flex justify-center items-center h-screen">
      <Spinner size="xl" />
    </div>
  );
});
ScreenSpinner.displayName = 'ScreenSpinner';

export const Loading = React.memo(({ className }: { className?: string }) => {
  return (
    <div className={cn('flex justify-center items-center py-16', className)}>
      <Spinner size="lg" />
    </div>
  );
});
Loading.displayName = 'Loading';

export function LoadingSkeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse bg-gray-200 dark:bg-gray-700 rounded', className)} />;
}

export function InlineSpinner({ text = 'Processing...' }: { text?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
      <span className="inline-block w-3 h-3 border-2 border-purple-500 border-r-transparent rounded-full animate-spin" />
      {text}
    </span>
  );
}
